import {combineLatest, merge, withLatestFrom} from 'rxjs';
import Big from 'big.js';
import {filter, map, scan, shareReplay, startWith} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {contractsCache$} from '@/config';
import {tradePair$} from '@/state/trade-pair';
import {contractMarket24hData$, contractMarket24hDataV2$} from '@/raw/market24h-contract';
import type {ws, ContractMarket, TradePair, ContractPair} from '@/types';

type ContractMarketData = ws.ContractMarketData;

const allContractMarketData$ = merge(contractMarket24hData$, contractMarket24hDataV2$).pipe(
  scan((p, c) => {
    c.forEach((o) => p.set(o.symbol, o));
    return p;
  }, new Map<string, ContractMarketData>()),
  withLatestFrom(tradePair$.pipe(startWith(null))),
  map(([markets, tradePair]) => {
    const market = markets.has(tradePair?.symbol);
    const map = market ? markets : markets.set(tradePair?.symbol, createInitContractMarketData(tradePair));
    return Array.from(map.values());
  })
);

export const contractMarket24h$ = combineLatest([contractsCache$, allContractMarketData$]).pipe(
  map(convertContractMarkets),
  filter((list: ContractMarket[]) => list.length > 0),
  shareReplay(1)
);

function convertContractMarkets([contractsCache, contractMarketData]): ContractMarket[] {
  return contractMarketData.map((o: ContractMarketData) => convertContractMarket(contractsCache, o)).filter(Boolean);
}

function convertContractMarket(
  contractsCache: Map<string, ContractPair>,
  contractMarketData: ContractMarketData
): ContractMarket {
  const {symbol} = contractMarketData;
  const contract = contractsCache.get(symbol);
  if (!contract) {
    return null;
  }

  const {
    close,
    high,
    low,
    open,
    indexPrice,
    markPrice,
    volume,
    turnover: turnoverEv,
    openInterest = 0,
    fundingRate,
    predFundingRate,
    bid,
    ask,
  } = contractMarketData;
  const {
    type,
    displaySymbol,
    pairName,
    baseCurrency,
    pricePrecision,
    priceScale,
    valuePrecision,
    valueScale,
    contractSize,
    status,
    contractSide,
    valueFactor,
    settleCurrencyDisplay,
    ratioScale,
    urlSymbol,
    perpProductSubType,
  } = contract;
  const turnover = numUtil.floor(turnoverEv, contractSide > 0 ? valuePrecision : 0, valueScale);
  const turnoverStr = numUtil.delimit(turnover);
  const change24hEp = close - open;
  const change24h = numUtil.floor(change24hEp, pricePrecision, priceScale);
  const changePercent = genChangePercent(close, open);
  const combinationTurnoverEv = contractSide < 0 ? volume : turnoverEv / valueFactor;
  if (!['Listed', 'Suspend'].includes(status)) {
    return createInit(contract);
  }
  const turnoverWithUSDRaw = Number(numUtil.floor(Big(turnover).times(indexPrice), pricePrecision, priceScale));
  return {
    urlSymbol,
    type,
    perpProductSubType,
    symbol,
    status,
    pairName,
    baseCurrency,
    symbolV2: pairName,
    displaySymbol,
    closeEp: close,
    openEp: open,
    indexPriceEp: indexPrice,
    indexPrice: numUtil.delimit(numUtil.floor(indexPrice, pricePrecision, priceScale)),
    markPriceEp: markPrice,
    markPrice: numUtil.delimit(numUtil.floor(markPrice, pricePrecision, priceScale)),
    close: numUtil.delimit(numUtil.floor(close, pricePrecision, priceScale)),
    highEp: high,
    high: numUtil.delimit(numUtil.floor(high, pricePrecision, priceScale)),
    lowEp: low,
    low: numUtil.delimit(numUtil.floor(low, pricePrecision, priceScale)),
    open: numUtil.delimit(numUtil.floor(open, pricePrecision, priceScale)),
    turnover,
    turnoverEv,
    turnoverStr: numUtil.abbreviateNumber(turnover),
    turnoverWithUSDRaw,
    turnoverWithUSD: numUtil.abbreviateNumber(
      numUtil.floor(Big(turnover).times(indexPrice), pricePrecision, priceScale)
    ),
    turnoverWU: turnoverStr + ' ' + settleCurrencyDisplay,
    openInterest,
    openInterestStr: numUtil.delimit(openInterest),
    volume,
    volume24hStr: numUtil.abbreviateNumber(Math.floor(volume * contractSize)),
    volume24hWithUSD: numUtil.abbreviateNumber(
      numUtil.floor(Big(volume).times(contractSize).times(indexPrice), pricePrecision, priceScale)
    ),
    fundingRate,
    fundingRateStr: numUtil.floor(fundingRate, 4, ratioScale - 2),
    predFundingRate,
    predFundingRateStr: numUtil.floor(predFundingRate, 4, ratioScale - 2),
    change24hEp,
    change24hStr: gen24hChangeStr(change24hEp, change24h),
    changePercent,
    changePercentStr: genChangePercentStr(changePercent),
    combinationTurnoverEv,
    contractSide,
    klineData: [],
    isCheapCopyToken: turnoverWithUSDRaw < 1e7,
    bidEp: bid,
    bid: numUtil.floor(bid, pricePrecision, priceScale),
    askEp: ask,
    ask: numUtil.floor(ask, pricePrecision, priceScale),
  };
}

function gen24hChangeStr(change24hEp, change24h): string {
  const prefix = change24hEp > 0 ? '+' : '';
  return prefix + change24h;
}

function genChangePercent(closeEp, openEp): number {
  if (closeEp === 0 || openEp === 0 || closeEp === openEp) {
    return 0;
  }
  const changeEp = closeEp - openEp;
  return (changeEp * 100) / openEp;
}

function genChangePercentStr(changePercent): string {
  if (changePercent === 0) {
    return '--%';
  }

  const prefix = changePercent > 0 ? '+' : '';
  const percent = numUtil.floor(changePercent, 2);
  return prefix + percent + '%';
}

function createInitContractMarketData(contract: TradePair): ContractMarketData {
  return {
    close: 0,
    fundingRate: 0,
    high: 0,
    indexPrice: 0,
    low: 0,
    markPrice: 0,
    open: 0,
    openInterest: 0,
    predFundingRate: 0,
    symbol: contract?.symbol,
    turnover: 0,
    volume: 0,
    bid: 0,
    ask: 0,
  };
}

function createInit({
  type,
  perpProductSubType,
  symbol,
  status,
  pairName,
  baseCurrency,
  displaySymbol,
  contractSide,
}): ContractMarket {
  return {
    type,
    perpProductSubType,
    symbol,
    status,
    symbolV2: pairName,
    pairName,
    baseCurrency,
    displaySymbol,
    closeEp: 0,
    openEp: 0,
    indexPriceEp: 0,
    indexPrice: '--',
    markPriceEp: 0,
    markPrice: '--',
    close: '--',
    highEp: 0,
    high: '--',
    lowEp: 0,
    low: '--',
    open: '--',
    turnoverEv: 0,
    turnoverStr: '--',
    turnoverWithUSDRaw: 0,
    turnoverWithUSD: '--',
    turnoverWU: '--',
    openInterest: 0,
    openInterestStr: '--',
    volume: 0,
    volume24hStr: '--',
    volume24hWithUSD: '--',
    fundingRate: 0,
    fundingRateStr: '--',
    predFundingRate: 0,
    predFundingRateStr: '--',
    change24hEp: 0,
    change24hStr: '--',
    changePercent: 0,
    changePercentStr: '--%',
    combinationTurnoverEv: 0,
    contractSide,
    klineData: [],
    turnover: '--',
    isCheapCopyToken: false,
    bidEp: 0,
    bid: '--',
    askEp: 0,
    ask: '--',
    urlSymbol: '',
  };
}
